import React, { useCallback, useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FaTags } from 'react-icons/fa';
import { MdKeyboardBackspace } from 'react-icons/md';
import {
  HeaderBackground,
  HeaderInfo,
  Input,
  Button,
  Label,
  FormGroup,
  Checkbox,
} from 'components';

import { IPaginate } from 'shared/interfaces';

import { useToast } from 'contexts/ToastProvider';

import api from 'services/api';

import { groupBy } from 'utils/groupBy';

import * as S from './styles';

interface FormData {
  name: string;
  access: string[];
}

const schema = Yup.object().shape({
  name: Yup.string().required('Permissão obrigatória'),
});

interface Permission {
  id: string;
  name: string;
  access: [{ id: string; name: string }];
}

interface PermissionAccess {
  id: string;
  slug: string;
  name: string;
  menu_name: string;
}

interface RouterParams {
  id?: string | undefined;
}

const PermissionForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      access: [],
    },
  });

  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState<Permission>();
  const [permissionAccess, setPermissionAccess] = useState([]);
  const { id } = useParams<RouterParams>();
  const history = useHistory();

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        setLoading(true);

        if (id) {
          const response = await api.put<Permission>(
            `/permissions/${id}`,
            data,
          );

          setPermission(response.data);
        } else {
          await api.post('/permissions', data);
          reset();

          history.push('/permissions');
        }
        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Salvo com sucesso',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Ops, Erro',
          description: 'Ocorreu um erro ao inserir um novo registro',
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, history, id, reset],
  );

  useEffect(() => {
    api
      .get<IPaginate<PermissionAccess>>('/access', {
        params: { page: 1, per_page: 200 },
      })
      .then((response) => {
        const { data } = response;

        const perm: any = data.data; // group by aceita tipo any

        const newPermissioAccess = groupBy(perm, 'menu_name');

        setPermissionAccess(newPermissioAccess);
      });
  }, []);

  useEffect(() => {
    if (id) {
      api.get<Permission>(`/permissions/${id}`).then((response) => {
        const { data } = response;

        setPermission(data);

        const permissionFormEdit = {
          ...data,
          access: data.access.map((key) => key.id),
        };

        reset(permissionFormEdit);
      });
    }
  }, [id, reset]);

  return (
    <S.Container>
      <HeaderBackground>
        <S.HeaderPageDatail>
          <S.BackLink>
            <Link to="/permissions">
              <MdKeyboardBackspace size={24} color="#fff" />
              Permissões
            </Link>
          </S.BackLink>
          <HeaderInfo
            title={id ? `Editando: ${permission?.name}` : 'Nova Permissão'}
            icon={FaTags}
          />
        </S.HeaderPageDatail>
      </HeaderBackground>

      <S.Panel>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label>Permissão</Label>
            <Input
              {...register('name')}
              type="text"
              errors={errors.name}
              name="name"
              placeholder="Permissão"
            />
          </FormGroup>

          <S.PermissionContainer>
            {Object.entries(permissionAccess).map((item) => (
              <div key={item[0]}>
                <S.TitlePermission>{item[0]}</S.TitlePermission>
                <Checkbox
                  {...register('access')}
                  name="access"
                  items={Array.from(item[1])}
                />
              </div>
            ))}
          </S.PermissionContainer>

          <S.Divider />

          <Button
            variant="primary"
            disabled={loading}
            loading={loading}
            type="submit"
            title="Salvar"
          >
            Salvar
          </Button>
        </form>
      </S.Panel>
    </S.Container>
  );
};

export default PermissionForm;
